import { css, useTheme } from '@emotion/react';
import cn from 'classnames';
import { isFunction, noop } from 'lodash';
import {
  cloneElement,
  type ForwardedRef,
  Fragment,
  forwardRef,
  type MouseEvent,
  memo,
  type ReactElement,
  type ReactNode,
} from 'react';

import { removeButtonAppearance } from '../../../../../utils/style';
import { CheckboxControl } from '../../../../forms/checkbox-control/CheckboxControl';
import { ICON_SIZES } from '../../../../general/icon/constants';
import { Typography } from '../../../../general/typography/Typography';
import * as styles from './ItemRenderer.styles';

export type ItemRendererProps = {
  /** Icon from @icon-park/react. */
  icon?: ReactElement;
  /** Text label. */
  label?: ReactNode;
  /**
   * Override default inner renderer.
   * If this is specified, icon and label are ignored.
   * Signature: ({ isSelected, isDisabled }) => React.Node.
   */
  render?: (params: { isSelected: boolean; isDisabled: boolean; isChecked: boolean }) => ReactNode;
  /** Called when clicking the item. */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Is the item selected. */
  isSelected?: boolean;
  /** Is the item disabled. */
  isDisabled?: boolean;
  /** Is the item active (i.e. if the sub menu is open). */
  isActive?: boolean;
  /** Is the item checkable. */
  isCheckable?: boolean;
  /** Is the item checked (only if isCheckable is true). */
  isChecked?: boolean;
  /** More content to the right of the item (like an arrow for SubList). */
  children?: ReactNode;
};

export const ItemRendererBase = forwardRef(
  (
    {
      icon = null,
      label = null,
      render = null,
      onClick = noop,
      isSelected = false,
      isDisabled = false,
      isActive = false,
      isChecked = false,
      isCheckable = false,
      children = null,
    }: ItemRendererProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    const theme = useTheme();

    return (
      <button
        ref={ref}
        type="button"
        disabled={isDisabled}
        onClick={onClick}
        className={cn({ isSelected, isActive })}
        css={[removeButtonAppearance, styles.containerStyle]}
      >
        {isFunction(render) ? (
          render({ isSelected, isDisabled, isChecked })
        ) : (
          <Fragment>
            {!!isCheckable && <CheckboxControl checked={isChecked} readOnly />}

            {!!icon &&
              cloneElement(icon, {
                size: ICON_SIZES.MEDIUM,
                fill: icon.props.fill || styles.getIconFills(theme, isDisabled, isSelected),
              })}

            <Typography
              variant={Typography.VARIANTS.BUTTON_MEDIUM_REGULAR}
              css={css`
                flex: 1;
                word-break: break-word;
              `}
            >
              {label}
            </Typography>
          </Fragment>
        )}

        {children}
      </button>
    );
  },
);

ItemRendererBase.displayName = 'ItemRenderer';

export const ItemRenderer = memo(ItemRendererBase);
